/**
 * @fileoverview Nomes de países localizados com `Intl.DisplayNames` (API nativa).
 * Não reutiliza outro módulo do BE para isto — não existia mapeamento língua→BCP 47
 * para regiões. O i18n (`vue-i18n` + JSON em `locales/general/`) trata strings da UI;
 * aqui só nomes de região derivados do código ISO em `countries.json`.
 *
 * **Sobre `TO_INTL_LOCALE`:** são só **excepções** onde o código de língua do produto
 * (`locales/languages.json`) não coincide com uma *locale* válida para nomes de região
 * em `Intl` (ex. `br` = Português BR → `pt-BR`; em ISO 639, `br` seria breton).
 * Todas as outras (`es`, `pt`, `fr`, `de`, `it`, …) usam-se **tal como estão**
 * em `intlLocaleForRegionNames` (`TO_INTL_LOCALE[k] ?? k`).
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames Intl.DisplayNames}
 */

/** @typedef {{ value: string, label: string, countryCode?: string }} CountrySelectOption */

/**
 * Excepções: código do BE → locale BCP 47 para `type: 'region'`.
 * @type {Readonly<Record<string, string>>}
 */
const TO_INTL_LOCALE = Object.freeze({
  br: 'pt-BR',
  zh: 'zh-CN',
  no: 'nb-NO',
  nb: 'nb-NO',
});

const hasIntlRegions =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function';

/**
 * Locale passada ao `Intl.DisplayNames` para nomes de países.
 * @param {string | null | undefined} lang
 * @returns {string}
 */
export const intlLocaleForRegionNames = (lang) => {
  if (lang == null || lang === '') return 'en';
  const k = String(lang).toLowerCase();
  return TO_INTL_LOCALE[k] ?? k;
};

/**
 * Nome de região (país) para um código ISO 3166-1 alpha-2.
 * @param {string | null | undefined} regionCode
 * @param {string | null | undefined} appLanguage Código activo do BE / i18n (ex. `es`)
 * @param {string | undefined} fallback Label inglês de `countries.json` se Intl falhar
 * @returns {string}
 */
export const getLocalizedRegionName = (regionCode, appLanguage, fallback) => {
  const code =
    regionCode == null ? '' : String(regionCode).trim().toUpperCase();
  if (!code) return fallback ?? '';

  if (!hasIntlRegions) return fallback ?? code;

  try {
    const display = new Intl.DisplayNames(
      [intlLocaleForRegionNames(appLanguage), 'en'],
      { type: 'region' },
    );
    const name = display.of(code);
    if (name && name !== code) return name;
  } catch {
    /* códigos desconhecidos ou ambiente sem suporte completo */
  }
  return fallback ?? code;
};

/**
 * Nova lista com `label` localizado; imutável face ao `applicationSetup.countries`.
 * @param {CountrySelectOption[] | null | undefined} countries
 * @param {string | null | undefined} appLanguage
 * @returns {CountrySelectOption[]}
 */
export const withLocalizedCountryLabels = (countries, appLanguage) =>
  Array.isArray(countries)
    ? countries.map((c) => {
        if (!c) return c;
        const v = c.value;
        if (v === '' || v == null) return { ...c };
        return {
          ...c,
          label: getLocalizedRegionName(v, appLanguage, c.label),
        };
      })
    : [];

/**
 * Localiza labels de estados sem mutar a lista original.
 * @param {{ value: string, label: string }[] | null | undefined} states
 * @param {(key: string) => string} translate
 * @param {string} [keyPrefix='general.usStates']
 * @returns {{ value: string, label: string }[]}
 */
export const withLocalizedStateLabels = (
  states,
  translate,
  keyPrefix = 'general.usStates',
) =>
  Array.isArray(states)
    ? states.map((state) => {
        if (!state) return state;
        const code =
          state.value == null ? '' : String(state.value).trim().toUpperCase();
        if (!code) return { ...state };

        const key = `${keyPrefix}.${code}`;
        const translated = translate?.(key);
        const resolved =
          translated && translated !== key ? translated : (state.label ?? code);

        return { ...state, label: resolved };
      })
    : [];
